import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiSearch,
  FiFilter,
  FiDownload,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiX,
  FiMoreVertical,
  FiChevronLeft,
  FiChevronRight,
  FiPackage,
  FiUserX,
  FiInfo,
  FiAlertCircle
} from "react-icons/fi";
import { toast } from "react-hot-toast";
import FirebaseProductService from "../../../services/product.service";
import { auth, onAuthStateChanged } from "../../../firebase/config";

/* -------------------- CONSTANTS -------------------- */
const CATEGORIES = [
  'Cereals',
  'Industrial Machinery',
  'Electronics & Electrical',
  'Raw Materials',
  'Finished Goods',
  'Tools & Equipment',
  'Safety Products',
  'Packaging Materials',
  'Chemicals & Lubricants',
  'Construction Materials',
  'Automotive Parts'
];

// HS_CODES constant removed as it's no longer needed

/* -------------------- UI COMPONENTS -------------------- */
const StatusBadge = ({ status, rejectionReason }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-green-600';
      case 'pending':
        return 'bg-yellow-500';
      case 'rejected':
        return 'bg-red-600';
      case 'draft':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(status)}`}>
        {status || 'PENDING'}
      </span>
    </div>
  );
};

// Rejection Reason Tooltip Component
const RejectionReasonTooltip = ({ reason, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
      />
      <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-72 bg-white rounded-lg shadow-xl border border-red-200 z-50">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FiAlertCircle className="w-5 h-5 text-red-600" />
              <h3 className="text-sm font-semibold text-gray-900">Rejection Reason</h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
          
          <div className="text-sm text-gray-700 bg-red-50 p-3 rounded-md border border-red-100">
            {reason || 'No reason provided'}
          </div>
          
          <div className="mt-3 pt-3 border-t border-gray-100">
            <button
              onClick={onClose}
              className="w-full py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Filter Dropdown Component - Show all statuses
const FilterDropdown = ({ isOpen, onClose, filters, setFilters }) => {
  if (!isOpen) return null;

  // Show all status options for filtering
  const statusOptions = ['ALL', 'PENDING', 'APPROVED', 'REJECTED'];
  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'name_asc', label: 'Name A-Z' },
    { value: 'name_desc', label: 'Name Z-A' },
    { value: 'price_asc', label: 'Price: Low to High' },
    { value: 'price_desc', label: 'Price: High to Low' },
    { value: 'quantity_asc', label: 'Quantity: Low to High' },
    { value: 'quantity_desc', label: 'Quantity: High to Low' }
  ];

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
      />
      <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Status Filter - All statuses */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <div className="space-y-2">
                {statusOptions.map((status) => (
                  <label key={status} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="status"
                      checked={filters.status === status}
                      onChange={() => setFilters(prev => ({ ...prev, status }))}
                      className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">{status}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                value={filters.sortBy}
                onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters Button */}
            <div className="pt-2">
              <button
                onClick={() => setFilters({ status: 'ALL', sortBy: 'newest' })}
                className="w-full text-sm text-orange-600 hover:text-orange-800 font-medium py-2 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Enhanced Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage, currentItemsCount }) => {
  const pages = [];

  if (totalPages <= 1) return null;

  // Always show first page
  pages.push(1);

  // Calculate page range
  let startPage = Math.max(2, currentPage - 1);
  let endPage = Math.min(totalPages - 1, currentPage + 1);

  // Add ellipsis after first page if needed
  if (startPage > 2) {
    pages.push('...');
  }

  // Add middle pages
  for (let i = startPage; i <= endPage; i++) {
    if (i > 1 && i < totalPages) {
      pages.push(i);
    }
  }

  // Add ellipsis before last page if needed
  if (endPage < totalPages - 1) {
    pages.push('...');
  }

  // Always show last page if there's more than one page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  // Calculate start and end item numbers
  const startItem = ((currentPage - 1) * itemsPerPage) + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-t border-gray-200 px-4 py-4 bg-white">
      {/* Item count info */}
      <div className="mb-3 sm:mb-0">
        <p className="text-sm text-gray-700">
          Showing <span className="font-medium">{startItem}</span> to{' '}
          <span className="font-medium">{endItem}</span> of{' '}
          <span className="font-medium">{totalItems}</span> results
          {currentItemsCount !== itemsPerPage && currentPage === totalPages && (
            <span className="text-gray-500 text-xs ml-2">
              (Page {currentPage} has {currentItemsCount} items)
            </span>
          )}
        </p>
      </div>

      {/* Pagination controls */}
      <div>
        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 ${
              currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <span className="sr-only">Previous</span>
            <FiChevronLeft className="h-4 w-4" aria-hidden="true" />
            <span className="ml-1 hidden sm:inline">Previous</span>
          </button>

          {/* Page numbers */}
          {pages.map((page, index) => (
            page === '...' ? (
              <span
                key={`ellipsis-${index}`}
                className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
              >
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 focus:outline-offset-0 ${
                  currentPage === page
                    ? 'z-10 bg-orange-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600'
                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                }`}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </button>
            )
          ))}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 ${
              currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <span className="sr-only">Next</span>
            <span className="mr-1 hidden sm:inline">Next</span>
            <FiChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </nav>
      </div>

      {/* Quick page jump for desktop */}
      <div className="hidden lg:flex items-center space-x-2 mt-3 sm:mt-0">
        <span className="text-sm text-gray-700">Go to page:</span>
        <div className="flex items-center">
          <input
            type="number"
            min="1"
            max={totalPages}
            defaultValue={currentPage}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= totalPages) {
                  onPageChange(page);
                }
              }
            }}
            className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
      </div>
    </div>
  );
};

/* -------------------- MOBILE CARD -------------------- */
const MobileProductCard = ({ product, onView, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [showRejectReason, setShowRejectReason] = useState(false);

  const getImageUrl = (product) => {
    if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      if (firstImage.data) {
        return firstImage.data;
      }
      if (firstImage.url) {
        return firstImage.url;
      }
    }
    return "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=120";
  };

  // Show edit/delete for PENDING and APPROVED products
  // Show only delete for REJECTED products
  const showActions = product.status === 'PENDING' || product.status === 'APPROVED' || product.status === 'REJECTED';

  return (
    <>
      <div className="flex items-start gap-3 p-3 border-b bg-white hover:bg-gray-50 transition-colors">
        {/* Product Image */}
        <img
          src={getImageUrl(product)}
          alt={product.name}
          className="w-12 h-12 rounded-md border object-cover flex-shrink-0"
          onError={(e) => {
            e.target.src = "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=120";
          }}
        />

        {/* Product Details */}
        <div className="flex-1 min-w-0">
          {/* Product Name and Status in one line */}
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold text-sm text-black truncate">{product.name}</p>
            <StatusBadge status={product.status} isMobile={true} />
          </div>
          
          {/* Category only - HS Code removed */}
          <p className="text-xs text-gray-600 mt-1 truncate">
            {product.category}
          </p>
          
          {/* Quantity and Price */}
          <div className="mt-2 flex items-center gap-3">
            <div className="flex items-center gap-1 text-xs text-gray-700">
              <FiPackage className="w-3 h-3 text-gray-500" />
              <span>{product.quantity || 0} {product.unit || 'PCS'}</span>
            </div>
            <div className="flex items-center gap-1 text-xs font-medium text-green-600">
              <span className="text-[11px]">₹</span>
              <span>{product.price || 0} {product.currency || 'INR'}</span>
            </div>
          </div>
          
          {/* Rejection Reason for Rejected Products */}
          {product.status === 'REJECTED' && product.rejectionReason && (
            <button
              onClick={() => setShowRejectReason(true)}
              className="mt-2 text-xs text-orange-600 hover:text-orange-800 font-medium flex items-center gap-1"
            >
              <FiInfo className="w-3 h-3" />
              View Rejection Reason
            </button>
          )}
        </div>

        {/* Actions Menu */}
        {showActions && (
          <div className="relative flex-shrink-0">
            <div className="relative">
              <button
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                onClick={() => setOpen(!open)}
              >
                <FiMoreVertical className="w-5 h-5 text-gray-600" />
              </button>

              {open && (
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setOpen(false)}
                />
              )}

              {open && (
                <div
                  className="absolute right-0 top-full -mt-4 bg-white border shadow-lg rounded-md w-32 z-50"
                  onMouseEnter={() => setOpen(true)}
                  onMouseLeave={() => setOpen(false)}
                >
                  <button
                    className="flex items-center gap-2 w-full px-3 py-2 text-xs hover:bg-gray-100 transition-colors text-gray-800 border-b"
                    onClick={() => {
                      onView(product);
                      setOpen(false);
                    }}
                  >
                    <FiEye className="w-3 h-3" /> View
                  </button>
                  
                  {/* Edit button - only for PENDING and APPROVED */}
                  {(product.status === 'PENDING' || product.status === 'APPROVED') && (
                    <button
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs hover:bg-gray-100 transition-colors text-gray-800 border-b"
                      onClick={() => {
                        onEdit(product);
                        setOpen(false);
                      }}
                    >
                      <FiEdit2 className="w-3 h-3" /> Edit
                    </button>
                  )}
                  
                  {/* View Rejection Reason button - only for REJECTED */}
                  {product.status === 'REJECTED' && product.rejectionReason && (
                    <button
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs text-orange-600 hover:bg-orange-50 transition-colors border-b"
                      onClick={() => {
                        setShowRejectReason(true);
                        setOpen(false);
                      }}
                    >
                      <FiInfo className="w-3 h-3" /> View Reason
                    </button>
                  )}
                  
                  {/* Delete button - for all statuses */}
                  <button
                    className="flex items-center gap-2 w-full px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
                    onClick={() => {
                      onDelete(product);
                      setOpen(false);
                    }}
                  >
                    <FiTrash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Rejection Reason Tooltip for Mobile */}
      {showRejectReason && product.status === 'REJECTED' && product.rejectionReason && (
        <RejectionReasonTooltip 
          reason={product.rejectionReason}
          isOpen={showRejectReason}
          onClose={() => setShowRejectReason(false)}
        />
      )}
    </>
  );
};

/* -------------------- DESKTOP ROW -------------------- */
const DesktopRow = ({ product, onView, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [showRejectReason, setShowRejectReason] = useState(false);

  const getImageUrl = (product) => {
    if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      if (firstImage.data) {
        return firstImage.data;
      }
      if (firstImage.url) {
        return firstImage.url;
      }
    }
    return "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=120";
  };

  // Show edit/delete for PENDING and APPROVED products
  // Show only delete for REJECTED products
  const showActions = product.status === 'PENDING' || product.status === 'APPROVED' || product.status === 'REJECTED';

  return (
    <>
      <tr className="border-b hover:bg-gray-50 transition-colors group">
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            <img
              src={getImageUrl(product)}
              className="w-10 h-10 rounded border object-cover"
              alt={product.name}
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=120";
              }}
            />
            <div>
              <span className="font-medium text-black block">{product.name}</span>
              <span className="text-xs text-gray-500">{product.description?.substring(0, 50)}...</span>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 text-black">{product.category}</td>
        {/* HS Code column removed */}
        <td className="px-6 py-4 text-black">
          <div className="flex items-center gap-1">
            <FiPackage className="w-3 h-3 text-gray-500" />
            <span>{product.quantity || 0} {product.unit || 'PCS'}</span>
          </div>
        </td>
        <td className="px-6 py-4 text-black">
          <div className="flex items-center gap-1 text-green-600">
            <span className="text-[12px] leading-none">₹</span>
            <span>{product.price || 0} {product.currency || 'INR'}</span>
          </div>
        </td>
        <td className="px-6 py-4">
          <StatusBadge status={product.status} />
        </td>
        <td className="px-6 py-4 relative">
          {showActions && (
            <div className="relative">
              <button
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                onClick={() => setOpen(!open)}
              >
                <FiMoreVertical className="w-5 h-5 text-gray-600" />
              </button>

              {open && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setOpen(false)}
                  />
                  <div
                    className="absolute right-16 top-full -mt-20 bg-white border shadow-lg rounded-md w-36 z-50"
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={() => setOpen(false)}
                  >
                    <button
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-gray-100 transition-colors text-gray-800 border-b"
                      onClick={() => {
                        onView(product);
                        setOpen(false);
                      }}
                    >
                      <FiEye className="w-4 h-4" /> View
                    </button>
                    
                    {/* Edit button - only for PENDING and APPROVED */}
                    {(product.status === 'PENDING' || product.status === 'APPROVED') && (
                      <button
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-gray-100 transition-colors text-gray-800 border-b"
                        onClick={() => {
                          onEdit(product);
                          setOpen(false);
                        }}
                      >
                        <FiEdit2 className="w-4 h-4" /> Edit
                      </button>
                    )}
                    
                    {/* View Rejection Reason button - only for REJECTED */}
                    {product.status === 'REJECTED' && product.rejectionReason && (
                      <button
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 transition-colors border-b"
                        onClick={() => {
                          setShowRejectReason(true);
                          setOpen(false);
                        }}
                      >
                        <FiInfo className="w-4 h-4" /> View Reason
                      </button>
                    )}
                    
                    {/* Delete button - for all statuses */}
                    <button
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      onClick={() => {
                        onDelete(product);
                        setOpen(false);
                      }}
                    >
                      <FiTrash2 className="w-4 h-4" /> Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </td>
      </tr>

      {/* Rejection Reason Tooltip for Desktop */}
      {showRejectReason && product.status === 'REJECTED' && product.rejectionReason && (
        <tr>
          <td colSpan="6" className="p-0">
            <RejectionReasonTooltip 
              reason={product.rejectionReason}
              isOpen={showRejectReason}
              onClose={() => setShowRejectReason(false)}
            />
          </td>
        </tr>
      )}
    </>
  );
};

/* -------------------- MAIN PRODUCTS PAGE -------------------- */
const MyProducts = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authUser, setAuthUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [authError, setAuthError] = useState(false);

  // Filter state - UPDATED: All statuses
  const [filters, setFilters] = useState({
    status: 'ALL',
    sortBy: 'newest'
  });

  // Check authentication and load products
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? `User logged in: ${user.uid}` : 'No user');
      
      if (user) {
        setAuthUser(user);
        setAuthError(false);
        try {
          await loadProducts();
        } catch (error) {
          console.error('Error loading products:', error);
          // Check if it's an auth-related error
          if (error.message?.includes('User document not found') || 
              error.message?.includes('permission') ||
              error.code?.includes('permission-denied')) {
            setAuthError(true);
            toast.error('Authentication error. Please try logging in again.');
          }
        }
      } else {
        console.log('No authenticated user, redirecting to login');
        toast.error('Please login to view your products');
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setAuthError(false);
      
      // Debug: Log current auth state
      const currentUser = auth.currentUser;
      console.log('Current auth user:', currentUser);
      
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      
      console.log('Loading products for user ID:', currentUser.uid);
      const result = await FirebaseProductService.getUserProducts();
      
      console.log('Products loaded successfully:', result.products?.length || 0, 'products');
      setProducts(result.products || []);
      setFilteredProducts(result.products || []);
    } catch (error) {
      console.error('Error loading products:', error);
      
      // Handle specific Firebase errors
      if (error.code === 'permission-denied') {
        setAuthError(true);
        toast.error('Permission denied. Please check your authentication.');
      } else if (error.message?.includes('User document not found')) {
        setAuthError(true);
        toast.error('User profile not found. Please contact support.');
      } else {
        toast.error(error.message || 'Failed to load products');
      }
      
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and search functionality
  useEffect(() => {
    let filtered = [...products];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(query) ||
        (product.category || '').toLowerCase().includes(query) || // HS Code removed from search
        (product.status || '').toLowerCase().includes(query) ||
        (product.rejectionReason || '').toLowerCase().includes(query)
      );
    }

    // Status filter - All statuses
    if (filters.status !== 'ALL') {
      filtered = filtered.filter(product => product.status === filters.status);
    }

    // Sort filter
    switch (filters.sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
        break;
      case 'name_asc':
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'name_desc':
        filtered.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        break;
      case 'price_asc':
        filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price_desc':
        filtered.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'quantity_asc':
        filtered.sort((a, b) => (a.quantity || 0) - (b.quantity || 0));
        break;
      case 'quantity_desc':
        filtered.sort((a, b) => (b.quantity || 0) - (a.quantity || 0));
        break;
      default:
        break;
    }

    setFilteredProducts(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchQuery, filters, products]);

  // Calculate paginated products
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage));
  const totalItems = filteredProducts.length;

  // Count active filters
  const activeFiltersCount = (filters.status !== 'ALL' ? 1 : 0) + (filters.sortBy !== 'newest' ? 1 : 0);

  // Navigation functions
  const handleAddProduct = () => {
    navigate('/seller/myproducts/add');
  };

  const handleViewProduct = (product) => {
    navigate(`/seller/myproducts/${product.id}`);
  };

  const handleEditProduct = (product) => {
    // Allow editing PENDING and APPROVED products only
    if (product.status === 'PENDING' || product.status === 'APPROVED') {
      navigate(`/seller/myproducts/${product.id}/edit`);
    } else if (product.status === 'REJECTED') {
      toast.error('REJECTED products cannot be edited. Please delete and create a new one.');
    } else {
      toast.error('Only PENDING or APPROVED products can be edited');
    }
  };

  const handleDeleteClick = (product) => {
    // Allow deleting all statuses (PENDING, APPROVED, REJECTED)
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  const confirmDeleteProduct = async () => {
    if (!selectedProduct) return;

    try {
      setDeleting(true);
      await FirebaseProductService.deleteProduct(selectedProduct.id);
      await loadProducts(); // Reload products after deletion
      setShowDeleteModal(false);
      setSelectedProduct(null);
      toast.success('Product deleted successfully!');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(error.message || 'Failed to delete product');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = () => {
    // Export functionality - HS Code removed from CSV
    const csvContent = [
      ['Name', 'Category', 'Quantity', 'Unit', 'Price', 'Currency', 'Status', 'Rejection Reason', 'Description', 'Created At'],
      ...filteredProducts.map(p => [
        p.name || '',
        p.category || '',
        p.quantity || 0,
        p.unit || 'PCS',
        p.price || 0,
        p.currency || 'INR',
        p.status || 'PENDING',
        p.rejectionReason || '',
        p.description || '',
        p.createdAt ? new Date(p.createdAt).toLocaleDateString() : 'N/A'
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `products_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success('Products exported successfully!');
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setFilters({
      status: 'ALL',
      sortBy: 'newest'
    });
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    // Scroll to top when changing page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentPage(page);
  };

  const handleLogout = () => {
    auth.signOut().then(() => {
      navigate('/login');
      toast.success('Logged out successfully');
    });
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-12 sm:pt-5 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-lg font-semibold text-gray-900">Loading products...</p>
        </div>
      </div>
    );
  }

  // Show auth error state
  if (authError) {
    return (
      <div className="min-h-screen bg-white pt-12 sm:pt-5 flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <FiUserX className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Authentication Error</h2>
          <p className="text-gray-600 mb-6">
            There was an issue with your authentication. Please try logging in again.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Go to Login
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-12 sm:pt-5">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-5 border-b gap-4">
        <div>
          <h1 className="text-xl font-bold text-black">My Products</h1>
          <p className="text-sm text-gray-600 mt-1">
            Total: {products.length} products | Showing: {filteredProducts.length} filtered
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-md font-medium hover:bg-orange-800 transition-colors disabled:opacity-50"
            onClick={handleAddProduct}
            disabled={!authUser}
          >
            <FiPlus /> Add New Product
          </button>
        </div>
      </div>

      {/* SEARCH AND FILTERS */}
      <div className="px-4 sm:px-6 py-4 border-b">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search products by name, category, status, or rejection reason..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={!authUser}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <div className="relative">
              <button
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                onClick={() => setShowFilters(!showFilters)}
                disabled={!authUser}
              >
                <FiFilter /> Filter
                {activeFiltersCount > 0 && (
                  <span className="bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              <FilterDropdown
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                filters={filters}
                setFilters={setFilters}
              />
            </div>
            <button
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-800 transition-colors disabled:opacity-50"
              onClick={handleExport}
              disabled={!authUser || filteredProducts.length === 0}
            >
              <FiDownload /> Export
            </button>
          </div>
        </div>

        {/* Active filters display */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages} | {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
            <span className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded">
              Note: Edit available for PENDING and APPROVED, Delete available for all statuses
            </span>
          </div>

          {(searchQuery || activeFiltersCount > 0) && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-orange-600 hover:text-orange-800 font-medium ml-2 disabled:opacity-50"
              disabled={!authUser}
            >
              Clear all filters
            </button>
          )}

          {/* Show active filters */}
          {searchQuery && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
              Search: "{searchQuery}"
              <button
                onClick={() => setSearchQuery('')}
                className="ml-1 text-blue-600 hover:text-blue-800"
              >
                <FiX className="w-3 h-3" />
              </button>
            </span>
          )}

          {filters.status !== 'ALL' && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800">
              Status: {filters.status}
              <button
                onClick={() => setFilters(prev => ({ ...prev, status: 'ALL' }))}
                className="ml-1 text-orange-600 hover:text-orange-800"
              >
                <FiX className="w-3 h-3" />
              </button>
            </span>
          )}

          {filters.sortBy !== 'newest' && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
              Sorted by: {filters.sortBy.replace('_', ' ')}
              <button
                onClick={() => setFilters(prev => ({ ...prev, sortBy: 'newest' }))}
                className="ml-1 text-green-600 hover:text-green-800"
              >
                <FiX className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      </div>

      {/* DESKTOP TABLE */}
      <div className="hidden lg:block">
        <div className="px-4 sm:px-6 py-6">
          {currentProducts.length > 0 ? (
            <>
              <div className="border rounded-md overflow-hidden relative">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-6 py-3 text-left text-gray-700 font-semibold">Product Name</th>
                      <th className="px-6 py-3 text-left text-gray-700 font-semibold">Category</th>
                      <th className="px-6 py-3 text-left text-gray-700 font-semibold">Quantity</th>
                      <th className="px-6 py-3 text-left text-gray-700 font-semibold">Price</th>
                      <th className="px-6 py-3 text-left text-gray-700 font-semibold">Status</th>
                      <th className="px-6 py-3 text-left text-gray-700 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentProducts.map((p) => (
                      <DesktopRow
                        key={p.id}
                        product={p}
                        onView={handleViewProduct}
                        onEdit={handleEditProduct}
                        onDelete={handleDeleteClick}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Enhanced Desktop Pagination */}
              {totalPages > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  currentItemsCount={currentProducts.length}
                />
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <FiPackage className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
              <p className="text-gray-600 mb-6">
                {products.length === 0 
                  ? "You haven't added any products yet. Start by adding your first product!"
                  : "Try adjusting your search or filters"}
              </p>
              <button
                onClick={handleAddProduct}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                disabled={!authUser}
              >
                <FiPlus className="inline-block w-4 h-4 mr-2" />
                {products.length === 0 ? 'Add Your First Product' : 'Add New Product'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* MOBILE LIST */}
      <div className="lg:hidden">
        {currentProducts.length > 0 ? (
          <>
            {currentProducts.map((p) => (
              <MobileProductCard
                key={p.id}
                product={p}
                onView={handleViewProduct}
                onEdit={handleEditProduct}
                onDelete={handleDeleteClick}
              />
            ))}

            {/* Mobile Pagination Controls */}
            {totalPages > 0 && (
              <div className="px-4 py-4 bg-white border-t">
                <div className="mb-3 text-center">
                  <p className="text-sm text-gray-700">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} products
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Page {currentPage} of {totalPages}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 text-sm font-medium rounded border ${
                      currentPage === 1
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors'
                    }`}
                  >
                    <FiChevronLeft className="inline-block w-4 h-4 mr-1" />
                    Previous
                  </button>
                  <div className="flex items-center space-x-2">
                    {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage <= 2) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 1) {
                        pageNum = totalPages - 2 + i;
                      } else {
                        pageNum = currentPage - 1 + i;
                      }

                      if (pageNum < 1 || pageNum > totalPages) return null;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 text-sm font-medium rounded ${
                            currentPage === pageNum
                              ? 'bg-orange-600 text-white'
                              : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    {totalPages > 3 && currentPage < totalPages - 1 && (
                      <span className="text-gray-500">...</span>
                    )}
                    {totalPages > 3 && currentPage < totalPages - 1 && (
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        className={`w-8 h-8 text-sm font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50`}
                      >
                        {totalPages}
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 text-sm font-medium rounded border ${
                      currentPage === totalPages
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors'
                    }`}
                  >
                    Next
                    <FiChevronRight className="inline-block w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <FiPackage className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-600">
              {products.length === 0 
                ? "No products found. Add your first product!"
                : "No products match your search or filters. Try adjusting them."}
            </p>
            {authUser && (
              <button
                onClick={handleAddProduct}
                className="mt-6 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                <FiPlus className="inline-block w-4 h-4 mr-2" />
                Add New Product
              </button>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedProduct && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => !deleting && setShowDeleteModal(false)}
            />

            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  {deleting ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" />
                  ) : (
                    <FiTrash2 className="w-6 h-6 text-red-600" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {deleting ? 'Deleting Product...' : 'Delete Product'}
                </h3>
                {!deleting && (
                  <>
                    <p className="text-sm text-gray-500 mb-4">
                      Are you sure you want to delete <span className="font-medium text-gray-900">"{selectedProduct.name}"</span>?
                    </p>
                    
                    {/* Show rejection reason if product is REJECTED */}
                    {selectedProduct.status === 'REJECTED' && selectedProduct.rejectionReason && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                        <div className="flex items-start gap-2">
                          <FiInfo className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                          <div className="text-left">
                            <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
                            <p className="text-sm text-red-600 mt-1">{selectedProduct.rejectionReason}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-500 mb-6">
                      Status: <span className={`font-medium ${
                        selectedProduct.status === 'PENDING' ? 'text-yellow-600' :
                        selectedProduct.status === 'APPROVED' ? 'text-green-600' :
                        'text-red-600'
                      }`}>{selectedProduct.status}</span>
                      {selectedProduct.status === 'REJECTED' && (
                        <span className="ml-2">This action cannot be undone.</span>
                      )}
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowDeleteModal(false)}
                        className="flex-1 py-2.5 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        disabled={deleting}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={confirmDeleteProduct}
                        className="flex-1 py-2.5 px-4 bg-red-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                        disabled={deleting}
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProducts;